import React, { useEffect, useState } from 'react'
import { propType } from 'graphql-anywhere'
import { useForm, Controller } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import ReactImgix from 'react-imgix'
import animateScroll from 'react-scroll/modules/mixins/animate-scroll'
import { Helmet } from 'react-helmet'
import classnames from 'classnames'

import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import CALLOUT_FRAGMENT from '../../../graphql/fragments/callout.gql'
import { getModalLocation } from '../../lib/modal'
import {
  MODALS, FORM_MESSAGES, SCREEN_SIZE_BREAKPOINTS
} from '../../constants'
import { useScreenWidth } from '../../hooks/use-screen-width'
import Button from '../button'
import Input from '../forms/input'
import { STORAGE_KEY_VOUCHER_PREFILLED } from '../../storage-keys'
import styles from './callout.css'

/**
 * A campaign callout component that presents a challenge question.
 *
 * 1. Displays a responsive question image, header, sub-header, and an answer input.
 * 2. User submits an answer; if correct, a confirmation view reveals a voucher code.
 * 3. On confirmation, unauthenticated users are redirected to the login modal;
 *    authenticated users are sent to the add-voucher modal to redeem.
 */
function CalloutComponent({ callout }) {
  const history = useHistory()
  const isAuthenticated = useIsAuthenticated()
  const screenWidth = useScreenWidth()
  const [error, setError] = useState('')
  const [isAnswerConfirmed, setIsAnswerConfirmed] = useState(false)

  useEffect(() => {
    if (isAnswerConfirmed) {
      animateScroll.scrollToTop({
        duration: 1000,
        smooth: 'easeOutCubic'
      })
    }
  }, [isAnswerConfirmed])

  const {
    desktopImageUrl,
    mobileImageUrl,
    preHeader,
    header,
    subHeader,
    answer,
    voucher,
    description,
    voucherExpiryText,
    voucherSuccessText,
    isBannerImage,
    skipVoucherCheck
  } = callout

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      answer: ''
    },
    mode: 'onChange'
  })

  /**
   * Validates the user's answer (case-insensitive).
   * On match: switches to the confirmation view.
   * On miss: shows an inline error and resets the input.
   */
  const onSubmit = (values) => {
    if (values.answer.trim().toLowerCase() === answer.trim().toLowerCase()) {
      setIsAnswerConfirmed(true)
      return
    }

    setError('Not quite right. Please try again.')
    reset({ answer: '' })
  }

  /**
   * Handles the "Redeem Offer" action after a correct answer.
   * - Unauthenticated users: redirected to home page with the login modal.
   * - Authenticated users: redirected to home page with the add-voucher modal.
   */
  const onConfirm = () => {
    setIsAnswerConfirmed(false)

    // Store the voucher so the AddVoucher form can prefill it
    sessionStorage.setItem(STORAGE_KEY_VOUCHER_PREFILLED, voucher.trim())

    if (!isAuthenticated) {
      history.push(getModalLocation({ pathname: '/' }, MODALS.qsParams.login))
      return
    }

    history.push(getModalLocation({ pathname: '/' }, MODALS.qsParams.addVoucher))
  }

  const redeemOfferButton = (
    <Button
      className={styles.button}
      text="Redeem Offer"
      onClick={onConfirm}
    />
  )
  const isMobileView = screenWidth <= SCREEN_SIZE_BREAKPOINTS.MIN_480
  const imageSrc = isMobileView ? mobileImageUrl : desktopImageUrl

  if (isAnswerConfirmed) {
    return (
      <div className={styles.wrapper}>
        <span className={styles.header}>
          Correct!
        </span>

        <span className={styles.subHeader}>
          Your voucher code is
          <span className={styles.voucher}>{` ${voucher.trim()}`}</span>
          <br />
          {voucherSuccessText}
        </span>

        <span className={styles.subHeader}>
          {voucherExpiryText}
        </span>

        {redeemOfferButton}
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className={classnames(styles.wrapper, { [styles.bannerWrapper]: isBannerImage })}>
        <div className={classnames(styles.imageWrapper, { [styles.bannerImageWrapper]: isBannerImage })}>
          <ReactImgix
            src={imageSrc}
            imgixParams={{ fit: 'clip', auto: 'format' }}
            alt={header || 'campaign image'}
            className={classnames(styles.image, { [styles.bannerImage]: isBannerImage })}
          />
        </div>

        <div className={styles.content}>
          <span className={styles.header}>
            {header}
          </span>

          <span className={styles.subHeader}>
            {preHeader}
          </span>

          <span className={styles.subHeader}>
            {subHeader}
          </span>

          {skipVoucherCheck ? redeemOfferButton : (
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="answer"
                control={control}
                rules={{
                  required: FORM_MESSAGES.required
                }}
                render={({ field }) => (
                  <Input
                    label=""
                    name={field.name}
                    className={styles.inputFrame}
                    inputClass={styles.input}
                    checkClass={styles.check}
                    exclamationClass={styles.exclamation}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(value) => {
                      setError('')
                      field.onChange(value)
                    }}
                    warning={errors.answer?.message || error}
                    isValid={Boolean(field.value && !errors.answer)}
                    placeholder="Type your answer here"
                  />
                )}
              />

              <Button
                className={styles.button}
                text="Check Answer"
              />
            </form>
          )}

          <span className={styles.description}>
            {description}
          </span>
        </div>
      </div>
    </>
  )
}

CalloutComponent.propTypes = {
  callout: propType(CALLOUT_FRAGMENT).isRequired
}

export default CalloutComponent
